// components/projects/create-project-modal.jsx
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostRequest } from '@/shared/hooks/requests.js';
import { PROJECT } from '@/shared/utils/urls.js';
import { toast } from 'sonner';

export function CreateProjectModal({ open, onOpenChange, setData }) {
  const { t } = useTranslation(['mainPage', 'common']);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const postProject = usePostRequest({ url: PROJECT });

  const handleCreate = async () => {
    // Валидация
    if (!name.trim()) {
      toast.error(t('projectModals.errors.nameRequired'));
      return;
    }
    if (!url.trim()) {
      toast.error(t('projectModals.errors.urlRequired'));
      return;
    }

    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol !== 'https:') {
        toast.error(t('projectModals.errors.urlHttps'));
        return;
      }
    } catch {
      toast.error(t('projectModals.errors.urlInvalid'));
      return;
    }

    const { success } = await postProject.request({
      data: {
        name: name.trim(),
        slug: 'str',
        origin_url: url,
        description: description.trim() || undefined,
      },
    });

    if (success) {
      toast.success(t('projectModals.create.success'));
      onOpenChange(false);
      setData();
      setName('');
      setUrl('');
      setDescription('');
    }
  };

  const handleClose = () => {
    setName('');
    setUrl('');
    setDescription('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-foreground">
            {t('projectModals.create.title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-4">
          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium text-foreground/80">
              {t('projectModals.fields.name')}
            </Label>
            <Input
              id="name"
              maxLength={255}
              placeholder={t('projectModals.fields.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12"
            />
          </div>

          {/* Project URL */}
          <div className="space-y-2">
            <Label htmlFor="url" className="text-sm font-medium text-foreground/80">
              {t('projectModals.fields.url')}
            </Label>
            <Input
              id="url"
              maxLength={500}
              type="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-12"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium text-foreground/80">
              {t('projectModals.fields.description')} <span className="text-muted-foreground font-normal">{t('projectModals.fields.optional')}</span>
            </Label>
            <Textarea
              id="description"
              placeholder={t('projectModals.fields.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px] resize-none"
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right">
              {description.length}/500
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={handleClose}
              className="h-11 px-6"
            >
              {t('common:cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={postProject.loading}
              className="h-11 px-6 text-white font-medium"
              style={{
                background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
              }}
            >
              {postProject.loading ? t('projectModals.create.creating') : t('projectModals.create.submit')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
